import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Sparkles, PanelLeft, PanelRight } from "lucide-react";

const mediaUrl = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export function HeroPromoManager() {
  const [side, setSide] = useState("right");
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ image_url: "", title: "", price: "", link: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/hero-promos");
      setSide(r.data.side || "right");
      setPromos(r.data.promos || []);
    } catch (e) { toast.error("Failed to load promos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const changeSide = async (s) => {
    setSide(s);
    try { await api.put("/admin/hero-promos-config", { side: s }); toast.success(`Promos will show on the ${s}`); }
    catch (e) { toast.error("Failed to update side"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const addPromo = async () => {
    if (!form.image_url) { toast.error("Please upload an image first"); return; }
    setBusy("add");
    try {
      await api.post("/admin/hero-promos", { ...form, sort: promos.length });
      toast.success("Promo added");
      setForm({ image_url: "", title: "", price: "", link: "" });
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add"); }
    finally { setBusy(""); }
  };

  const toggle = async (p) => {
    setBusy(p.promo_id);
    try { await api.put(`/admin/hero-promos/${p.promo_id}`, { active: !p.active }); await load(); }
    catch (e) { toast.error("Failed"); } finally { setBusy(""); }
  };
  const remove = async (p) => {
    if (!window.confirm("Delete this promo?")) return;
    setBusy(p.promo_id);
    try { await api.delete(`/admin/hero-promos/${p.promo_id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error("Failed"); } finally { setBusy(""); }
  };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= promos.length) return;
    const a = promos[idx], b = promos[j];
    setBusy(a.promo_id);
    try {
      await api.put(`/admin/hero-promos/${a.promo_id}`, { sort: j });
      await api.put(`/admin/hero-promos/${b.promo_id}`, { sort: idx });
      await load();
    } catch (e) { toast.error("Failed to reorder"); } finally { setBusy(""); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5">
      <Card className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-2">
          <Sparkles className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <p className="font-semibold text-sm">Homepage promo showcase</p>
            <p className="text-xs text-muted-foreground max-w-md">Upload sponsored products shown as a rotating big promo + thumbnails next to your hero headline.</p>
          </div>
        </div>
        <div className="inline-flex rounded-full border p-1 bg-muted/40 shrink-0" data-testid="promo-side-toggle">
          <button data-testid="promo-side-left" onClick={() => changeSide("left")} className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${side === "left" ? "bg-background shadow-sm" : "text-muted-foreground"}`}><PanelLeft className="w-4 h-4" /> Left</button>
          <button data-testid="promo-side-right" onClick={() => changeSide("right")} className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${side === "right" ? "bg-background shadow-sm" : "text-muted-foreground"}`}><PanelRight className="w-4 h-4" /> Right</button>
        </div>
      </Card>

      <Card className="p-4 space-y-3" data-testid="promo-add-form">
        <p className="font-semibold text-sm">Add a promo</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="w-full sm:w-40 shrink-0">
            {form.image_url ? (
              <img src={mediaUrl(form.image_url)} alt="preview" className="w-full h-28 object-cover rounded-lg border" />
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} className="w-full h-28 rounded-lg border border-dashed flex flex-col items-center justify-center gap-1 text-xs text-muted-foreground hover:bg-muted/40" data-testid="promo-upload-btn">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                {uploading ? "Uploading…" : "Upload image"}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="promo-file-input" />
            {form.image_url && <button className="text-xs text-primary mt-1" onClick={() => setForm((f) => ({ ...f, image_url: "" }))}>Change image</button>}
          </div>
          <div className="flex-1 grid gap-2">
            <Input data-testid="promo-title-input" placeholder="Title (e.g. Ankara Gown Sale)" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="promo-price-input" placeholder="Price (e.g. ₦12,500 or €25)" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
              <Input data-testid="promo-link-input" placeholder="Link (e.g. /product/abc)" value={form.link} onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))} />
            </div>
            <Button data-testid="promo-add-btn" onClick={addPromo} disabled={busy === "add" || uploading} className="rounded-full w-full sm:w-auto justify-self-start px-6">
              {busy === "add" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Add promo
            </Button>
          </div>
        </div>
      </Card>

      {promos.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground text-sm" data-testid="no-promos">No promos yet. Upload one above to activate the live hero.</Card>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {promos.map((p, i) => (
            <Card key={p.promo_id} className="p-3 flex items-center gap-3 min-w-0 overflow-hidden" data-testid={`promo-row-${p.promo_id}`}>
              <img src={mediaUrl(p.image_url)} alt={p.title} className="w-20 h-20 object-cover rounded-lg border shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{p.title || "(no title)"}</p>
                <p className="text-sm text-muted-foreground truncate">{p.price || "—"} · {p.link}</p>
                <Badge className={p.active ? "bg-green-600 text-white mt-1" : "bg-muted text-muted-foreground mt-1"}>{p.active ? "Active" : "Hidden"}</Badge>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0 || busy === p.promo_id} onClick={() => move(i, -1)} data-testid={`promo-up-${p.promo_id}`}><ArrowUp className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === promos.length - 1 || busy === p.promo_id} onClick={() => move(i, 1)} data-testid={`promo-down-${p.promo_id}`}><ArrowDown className="w-4 h-4" /></Button>
              </div>
              <Button size="sm" variant="outline" className="shrink-0" onClick={() => toggle(p)} disabled={busy === p.promo_id} data-testid={`promo-toggle-${p.promo_id}`}>
                {p.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              <Button size="sm" variant="outline" className="border-red-500 text-red-600 hover:bg-red-50 shrink-0" onClick={() => remove(p)} disabled={busy === p.promo_id} data-testid={`promo-delete-${p.promo_id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
